/**
 * 账单审核抽屉 — 嵌入式复用 InvoiceReviewPanel
 *
 * 设计文档: docs/DATA_LAYER_REDESIGN.md
 *
 * 策略：
 *   点击 Tab 2 列表行 → 从右侧滑出全宽抽屉
 *   → 内嵌 InvoiceReviewPanel（通过 invoiceDataSourceId prop + 新的聚合 API）
 *   → 用户在抽屉内完成：查看制表校验数据、编辑行项目、表格修正 Fixer、保存、提交审核
 *   → onStatusChange 回调通知 BatchContext 刷新
 *
 * 分屏联动：
 *   当用户在审核页面中打开「分屏视图」时，Drawer 视觉上关闭（滑出视口）以释放空间，
 *   但子组件树保持挂载——SplitViewModal 通过 React Portal 渲染到 document.body，
 *   不受 Drawer 视觉状态影响。分屏关闭时 Drawer 自动恢复。
 *
 * 标题行合并：
 *   Drawer 自身不渲染标题栏（headerRender={false}），全部信息和按钮由内部的
 *   ReconciliationHeader 统一承载，避免两行重复信息。关闭按钮集成到 ReconciliationHeader 中。
 */
import React, { useState, useCallback } from "react";
import { Drawer } from "antd";
import InvoiceReviewPanel from "../review/InvoiceReviewPanel";

const InvoiceDetailDrawer = ({ open, record, onClose, onStatusChange }) => {
    // 分屏视图激活时，Drawer 视觉关闭但不销毁内容
    const [hiddenBySplitView, setHiddenBySplitView] = useState(false);

    const handleSplitViewChange = useCallback((visible) => {
        setHiddenBySplitView(visible);
    }, []);

    // 用户手动关闭 Drawer（非分屏联动），重置状态后走正常关闭流程
    const handleClose = useCallback(() => {
        setHiddenBySplitView(false);
        onClose();
    }, [onClose]);

    if (!record) return null;

    // Drawer 视觉上打开 = 外部 open 为 true 且未被分屏隐藏
    // destroyOnClose 仅在非分屏隐藏时启用，防止分屏模式下 Provider 树被销毁
    return (
        <Drawer
            placement="right"
            width="85vw"
            open={open && !hiddenBySplitView}
            onClose={handleClose}
            destroyOnClose={!hiddenBySplitView}
            closable={false}
            headerStyle={{ display: "none" }}
            styles={{
                body: { padding: 0, overflow: "auto" },
            }}
        >
            {/* 内容持续渲染条件：外部 open 为 true（不管是否被分屏隐藏） */}
            {open && record?.id && (
                <InvoiceReviewPanel
                    invoiceDataSourceId={record.id}
                    onStatusChange={onStatusChange}
                    onSplitViewChange={handleSplitViewChange}
                    onCloseDrawer={handleClose}
                    drawerRecord={record}
                />
            )}
        </Drawer>
    );
};

export default InvoiceDetailDrawer;
